import type { FC, ReactNode } from 'react'
import type { ToolSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ToolBadges, ScoreBadge } from '@/components/directory/tool-badges'
import { ToolLogo } from '@/components/directory/tool-logo'

interface RankedToolRowProps {
    tool: ToolSummary
    rank?: number
    variant?: 'compact' | 'full'
}

const stretchedLink = 'after:absolute after:inset-0 after:content-[""] focus-visible:outline-none focus-visible:after:rounded-[inherit] focus-visible:after:outline-2 focus-visible:after:outline-primary'

const VisitLink: FC<{ tool: ToolSummary, compact?: boolean }> = ({ tool, compact }): ReactNode => {
    if (!tool.website_url || tool.discontinued) return null

    if (compact) {
        return (
            <a
                href={tool.website_url}
                target='_blank'
                rel='noopener noreferrer nofollow'
                aria-label={`Visit the ${tool.name} website (opens in a new tab)`}
                className='relative z-10 inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-primary'
            >
                <ArrowUpRight className='size-4' aria-hidden='true' />
            </a>
        )
    }

    return (
        <a
            href={tool.website_url}
            target='_blank'
            rel='noopener noreferrer nofollow'
            aria-label={`Visit the ${tool.name} website (opens in a new tab)`}
            className='relative z-10 inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-primary'
        >
            Visit
            <ArrowUpRight className='size-4' aria-hidden='true' />
        </a>
    )
}

export const RankedToolRow: FC<RankedToolRowProps> = ({ tool, rank, variant = 'compact' }): ReactNode => {
    const podium = rank !== undefined && rank <= 3

    if (variant === 'compact') {
        return (
            <li className='group relative flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-secondary/70'>
                {rank !== undefined && (
                    <span className={cn('w-5 shrink-0 text-center font-mono text-xs tabular-nums', podium ? 'text-primary' : 'text-muted-foreground')}>
                        {rank}
                    </span>
                )}

                <ToolLogo tool={tool} size='sm' />

                <div className='flex min-w-0 flex-1 items-center gap-2'>
                    <Link href={`/tools/${tool.slug}`} className={cn('truncate text-sm font-semibold text-foreground', stretchedLink)}>
                        <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                    </Link>

                    <ToolBadges tool={tool} />
                </div>

                {tool.score !== null && <ScoreBadge score={tool.score} />}

                <VisitLink tool={tool} compact />
            </li>
        )
    }

    return (
        <li className='group relative flex gap-4 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/40 sm:items-center sm:p-5'>
            {rank !== undefined && (
                <span className={cn('w-7 shrink-0 pt-2.5 text-center font-mono text-base font-semibold tabular-nums sm:pt-0', podium ? 'text-primary' : 'text-muted-foreground')}>
                    {rank}
                </span>
            )}

            <ToolLogo tool={tool} size='md' />

            <div className='min-w-0 flex-1'>
                <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1'>
                    <h3 className='font-heading text-lg font-semibold leading-snug text-foreground'>
                        <Link href={`/tools/${tool.slug}`} className={stretchedLink}>
                            <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                        </Link>
                    </h3>

                    <ToolBadges tool={tool} />
                </div>

                {tool.maker && (
                    <p className='mt-0.5 text-xs text-muted-foreground'>
                        by <span data-mdlfy-cms={`tools:${tool.id}:maker`}>{tool.maker}</span>
                    </p>
                )}

                {tool.tagline && (
                    <p className='mt-2 text-sm leading-relaxed text-foreground/85' data-mdlfy-cms={`tools:${tool.id}:tagline`}>
                        {tool.tagline}
                    </p>
                )}

                {tool.best_for && (
                    <p className='mt-1.5 text-xs text-muted-foreground'>
                        <span className='font-semibold uppercase tracking-wider'>Best for</span>{' '}
                        <span data-mdlfy-cms={`tools:${tool.id}:best_for`}>{tool.best_for}</span>
                    </p>
                )}

                <div className='mt-3 flex items-center gap-2 sm:hidden'>
                    {tool.score !== null && <ScoreBadge score={tool.score} />}
                    <VisitLink tool={tool} />
                </div>
            </div>

            <div className='hidden shrink-0 items-center gap-3 sm:flex'>
                {tool.score !== null && <ScoreBadge score={tool.score} />}
                <VisitLink tool={tool} />
            </div>
        </li>
    )
}

interface RankedToolListProps {
    tools: ToolSummary[]
    emptyMessage?: string
}

export const RankedToolList: FC<RankedToolListProps> = ({ tools, emptyMessage = 'No tools here yet.' }): ReactNode => {
    if (tools.length === 0) {
        return (
            <p className='rounded-2xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground'>
                {emptyMessage}
            </p>
        )
    }

    return (
        <ol className='space-y-3'>
            {tools.map((tool, index) => (
                <RankedToolRow key={tool.id} tool={tool} rank={index + 1} variant='full' />
            ))}
        </ol>
    )
}
